import { SuperOpsState } from "./interfaces";
import { SuperOpsWidgetName } from "../utils/SuperOpsRouteDetection";

/**
 * Thin typed wrapper around the SuperOps host JS SDK (loaded from
 * REACT_APP_SUPEROPS_SDK_URL). Before `init` runs, `window.superopsSdk`
 * exposes only `init`; afterwards the host swaps it for the full action API
 * (navigateTo, on, isReady, …). Every method here guards for the not-yet-ready
 * case so callers can no-op safely instead of throwing.
 */

export type SuperOpsRestoreDataCallback = (
	saved: SuperOpsState | undefined
) => void;

export type SuperOpsPersistDataCallback = () => SuperOpsState;

interface SuperOpsSdk {
	init: (widgetName: string) => void;
	navigateTo?: (path: string) => void;
	on?: (eventName: string, callback: (payload?: unknown) => unknown) => void;
	off?: (eventName: string, callback: (payload?: unknown) => unknown) => void;
	isReady?: () => boolean;
}

declare global {
	interface Window {
		superopsSdk?: SuperOpsSdk;
	}
}

const getSdk = (): SuperOpsSdk | undefined => window.superopsSdk;

let hostWidgetName: SuperOpsWidgetName | null = null;

const setHostWidgetName = (widgetName: SuperOpsWidgetName | null): void => {
	hostWidgetName = widgetName;
};

const getHostWidgetName = (): SuperOpsWidgetName | null => hostWidgetName;

const init = (widgetName: string): void => {
	const sdk = getSdk();
	if (sdk && typeof sdk.init === "function") {
		sdk.init(widgetName);
	}
};

const isReady = (): boolean => {
	const sdk = getSdk();
	return !!sdk && typeof sdk.isReady === "function" && sdk.isReady();
};

const navigateToClient = (clientId: string): void => {
	const sdk = getSdk();
	if (clientId && sdk && typeof sdk.navigateTo === "function") {
		sdk.navigateTo(`/client/${clientId}/detail`);
	}
};

const onRestoreData = (callback: SuperOpsRestoreDataCallback): void => {
	const sdk = getSdk();
	if (sdk && typeof sdk.on === "function") {
		sdk.on("restoreData", (saved?: unknown) => {
			callback((saved ?? undefined) as SuperOpsState | undefined);
		});
	}
};

const onPersistData = (callback: SuperOpsPersistDataCallback): void => {
	const sdk = getSdk();
	if (sdk && typeof sdk.on === "function") {
		sdk.on("persistData", () => callback());
	}
};

export const SuperOpsSdkService = {
	init,
	isReady,
	navigateToClient,
	onRestoreData,
	onPersistData,
	setHostWidgetName,
	getHostWidgetName
};
